// Serializable definition of a custom KPI dashboard report. A `ReportConfig`
// is what gets persisted in `SavedReport.config` (JSON) on the API and drives
// the config-driven dashboard render on the UI.
//
// Two kinds of group exist:
//
//   - `builtin`  — references a canonical `KPI_METRIC_GROUPS` slug and inherits
//                  that group's full legacy behavior: prefs-driven dynamic
//                  metric resolution (e.g. revenueBreakdown / totalBusinessRevenue
//                  / gaRevenue), connector-gating + "connect this service" setup
//                  prompts, and any special rendering (the custom-spend editor on
//                  `adSpend`, the separate brand-value cube query on
//                  `brandValueBreakdown`, conditional marginal metrics, etc.).
//                  The DEFAULT report is composed entirely of builtin groups, so
//                  it renders identically to the legacy hardcoded dashboard.
//
//   - `custom`   — a plain group that renders exactly the listed metric keys, in
//                  order. No connector-gating and no dynamic resolution. Metric
//                  labels/types/suffixes are resolved from the metric catalog at
//                  render time, so metric names are never stored here and stay
//                  consistent across reports (and can't be edited by users).

import { parseCalculatedMetrics, type CalculatedMetricDef } from './calculated-metrics';
import type { KpiTableSlug } from './metric-groups';

/**
 * Bump when the persisted `ReportConfig` JSON shape changes in a
 * backwards-incompatible way. Consumers can use this to migrate old configs.
 */
export const REPORT_CONFIG_VERSION = 1;

export interface BuiltinReportGroup {
  kind: 'builtin';
  /** Stable id for drag/drop + React keys. Defaults to the slug. */
  id: string;
  slug: KpiTableSlug;
}

/** How a custom group renders on the dashboard. Absent = 'table'. */
export type GroupVisualization = 'table' | 'line' | 'bar';

const GROUP_VISUALIZATIONS: readonly GroupVisualization[] = ['table', 'line', 'bar'];

function isGroupVisualization(value: unknown): value is GroupVisualization {
  return typeof value === 'string' && (GROUP_VISUALIZATIONS as readonly string[]).includes(value);
}

export interface CustomReportGroup {
  kind: 'custom';
  id: string;
  title: string;
  /** Ordered metric keys (catalog keys) rendered as rows in this group. */
  metricKeys: string[];
  /**
   * Subset of `metricKeys` that should additionally render a week-over-week
   * growth row (the existing absolute marginal-delta behavior). Maps onto the
   * legacy `marginalMetrics` prop at render time.
   */
  wowGrowthMetricKeys?: string[];
  showTotals?: boolean;
  /**
   * How the group renders on the dashboard: as the classic KPI table or as a
   * line/bar chart over the same rows. Optional and additive (absent =
   * 'table'), so the config version does not need to bump. Parsed leniently:
   * an unrecognized value is dropped, never a parse failure.
   */
  visualization?: GroupVisualization;
}

export type ReportGroup = BuiltinReportGroup | CustomReportGroup;

export interface ReportConfig {
  version: number;
  groups: ReportGroup[];
  /**
   * Report-scoped user-defined calculated metrics, referenced from a group's
   * metricKeys as `calc.<id>`. Optional and additive, so the config version
   * does not need to bump. Validated leniently: malformed defs are dropped.
   */
  calculatedMetrics?: CalculatedMetricDef[];
}

export function isBuiltinGroup(group: ReportGroup): group is BuiltinReportGroup {
  return group.kind === 'builtin';
}

export function isCustomGroup(group: ReportGroup): group is CustomReportGroup {
  return group.kind === 'custom';
}

/**
 * Best-effort runtime parse of an untrusted `config` value (e.g. loaded from the
 * `SavedReport.config` JSON column). Returns `null` when the shape is invalid so
 * callers can fall back to the default report instead of throwing. This is a
 * trust-boundary check: only the overall shape is validated here; metric keys
 * and slugs are resolved leniently against the catalog/registry at render time.
 */
export function parseReportConfig(value: unknown): ReportConfig | null {
  if (typeof value !== 'object' || value === null) {
    return null;
  }
  const candidate = value as { version?: unknown; groups?: unknown; calculatedMetrics?: unknown };
  if (typeof candidate.version !== 'number' || !Array.isArray(candidate.groups)) {
    return null;
  }

  // Report-scoped calculated metrics: validated leniently (malformed defs are
  // dropped, a non-array is ignored) so a bad calc never blanks an otherwise
  // valid report — mirroring how unknown metric keys degrade at render.
  const calculatedMetrics = parseCalculatedMetrics(candidate.calculatedMetrics);

  const groups: ReportGroup[] = [];
  for (const raw of candidate.groups) {
    if (typeof raw !== 'object' || raw === null) {
      return null;
    }
    const group = raw as Record<string, unknown>;

    if (group.kind === 'builtin') {
      if (typeof group.slug !== 'string') {
        return null;
      }
      groups.push({
        kind: 'builtin',
        id: typeof group.id === 'string' ? group.id : group.slug,
        slug: group.slug as KpiTableSlug,
      });
      continue;
    }

    if (group.kind === 'custom') {
      if (typeof group.id !== 'string' || typeof group.title !== 'string' || !Array.isArray(group.metricKeys)) {
        return null;
      }
      if (!group.metricKeys.every((key) => typeof key === 'string')) {
        return null;
      }
      const wow = group.wowGrowthMetricKeys;
      if (wow !== undefined && (!Array.isArray(wow) || !wow.every((key) => typeof key === 'string'))) {
        return null;
      }
      groups.push({
        kind: 'custom',
        id: group.id,
        title: group.title,
        metricKeys: group.metricKeys as string[],
        ...(wow !== undefined ? { wowGrowthMetricKeys: wow as string[] } : {}),
        ...(typeof group.showTotals === 'boolean' ? { showTotals: group.showTotals } : {}),
        // Lenient like calculatedMetrics: copy only a recognized literal;
        // anything else is dropped (absent = table) rather than failing.
        ...(isGroupVisualization(group.visualization) ? { visualization: group.visualization } : {}),
      });
      continue;
    }

    return null;
  }

  return {
    version: candidate.version,
    groups,
    ...(calculatedMetrics.length > 0 ? { calculatedMetrics } : {}),
  };
}
